import { createElement, useState, type ReactNode } from 'react';
import { Link } from 'expo-router';
import {
  Globe,
  MonitorSmartphone,
  Share,
  Smartphone,
  X,
} from 'lucide-react-native';
import {
  Image,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { PublicPageShell } from '@/src/components/PublicPageShell';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type InstallStep = {
  body: string;
  title: string;
};

type InstallVideo = {
  poster: string;
  source: string;
  title: string;
};

const iphoneInstallVideo: InstallVideo = {
  poster: '/install/frienzzi-install-iphone-poster.jpg',
  source: '/install/frienzzi-install-iphone.mp4',
  title: 'Install Frienzzi on iPhone',
};

const installGuides: {
  accent: string;
  icon: ReactNode;
  steps: InstallStep[];
  title: string;
  video?: InstallVideo;
}[] = [
  {
    accent: colors.primary,
    icon: <Smartphone color={colors.surface} size={34} strokeWidth={2.5} />,
    title: 'iPhone and iPad Safari',
    video: iphoneInstallVideo,
    steps: [
      {
        title: 'Open Frienzzi in Safari',
        body: 'Go to frienzzi.com in Safari. iOS only supports Home Screen web apps from Safari.',
      },
      {
        title: 'Tap Share',
        body: 'Use the Share button in the Safari toolbar.',
      },
      {
        title: 'Add to Home Screen',
        body: 'Choose Add to Home Screen, confirm the name Frienzzi, then tap Add.',
      },
      {
        title: 'Open the installed app',
        body: 'Launch Frienzzi from your Home Screen, then enable push notifications when prompted.',
      },
    ],
  },
  {
    accent: colors.secondary,
    icon: <Globe color={colors.surface} size={34} strokeWidth={2.5} />,
    title: 'Android Chrome',
    steps: [
      {
        title: 'Open frienzzi.com',
        body: 'Use Chrome on Android and sign in to your Frienzzi account.',
      },
      {
        title: 'Tap the browser menu',
        body: 'Open the three-dot menu and choose Install app or Add to Home screen.',
      },
      {
        title: 'Confirm install',
        body: 'Chrome adds Frienzzi to your app drawer and Home Screen.',
      },
      {
        title: 'Turn on notifications',
        body: 'Open the installed Frienzzi app and tap Enable notifications when you are ready.',
      },
    ],
  },
  {
    accent: colors.accent,
    icon: (
      <MonitorSmartphone color={colors.surface} size={34} strokeWidth={2.5} />
    ),
    title: 'Desktop Chrome and Edge',
    steps: [
      {
        title: 'Visit Frienzzi',
        body: 'Open frienzzi.com in Chrome or Edge on your computer.',
      },
      {
        title: 'Use the install icon',
        body: 'Click the install icon in the address bar, or open the browser menu and choose Install Frienzzi.',
      },
      {
        title: 'Pin it where you work',
        body: 'Keep Frienzzi in your dock, taskbar, or launcher for quick access.',
      },
      {
        title: 'Enable push if wanted',
        body: 'Installed desktop PWAs can receive browser push notifications after you grant permission.',
      },
    ],
  },
  {
    accent: colors.warmAccent,
    icon: <Share color={colors.surface} size={34} strokeWidth={2.5} />,
    title: 'Desktop Safari',
    steps: [
      {
        title: 'Open Safari',
        body: 'Go to frienzzi.com in Safari on macOS.',
      },
      {
        title: 'Add to Dock',
        body: 'Use File, then Add to Dock when Safari offers web app installation.',
      },
      {
        title: 'Open from Dock',
        body: 'Launch Frienzzi like a standalone app.',
      },
      {
        title: 'Manage notifications',
        body: 'Use Frienzzi Settings to enable or disable browser notification types.',
      },
    ],
  },
];

function DeviceIllustration({
  accent,
  icon,
}: {
  accent: string;
  icon: ReactNode;
}) {
  return (
    <View style={[styles.illustration, { backgroundColor: accent }]}>
      <View style={styles.deviceShell}>
        <View style={styles.deviceTopBar} />
        <View style={styles.deviceLogo}>{icon}</View>
        <View style={styles.deviceLineWide} />
        <View style={styles.deviceLine} />
        <View style={styles.deviceButton} />
      </View>
    </View>
  );
}

function InstallVideoPlayer({ video }: { video: InstallVideo }) {
  if (Platform.OS === 'web') {
    return createElement('video', {
      controls: true,
      playsInline: true,
      poster: video.poster,
      src: video.source,
      style: {
        backgroundColor: '#000000',
        borderRadius: 8,
        display: 'block',
        height: '100%',
        maxHeight: '78vh',
        objectFit: 'contain',
        width: '100%',
      },
    });
  }

  return (
    <View style={styles.nativeVideoFallback}>
      <Image
        accessibilityLabel=""
        resizeMode="cover"
        source={{ uri: video.poster }}
        style={styles.nativeVideoPoster}
      />
      <Text style={styles.nativeVideoText}>
        Open frienzzi.com/install in Safari to watch the iPhone install video.
      </Text>
    </View>
  );
}

export default function InstallScreen() {
  const [activeVideo, setActiveVideo] = useState<InstallVideo | null>(null);

  return (
    <>
      <PublicPageShell
        title="Install Frienzzi"
        subtitle="Add Frienzzi to your Home Screen or desktop for faster access and optional push notifications."
      >
        <View style={styles.heroCard}>
          <Text style={styles.heroTitle}>
            Install first. Enable notifications only when you choose.
          </Text>
          <Text style={styles.heroText}>
            Frienzzi will never trigger the browser notification permission
            prompt automatically on page load. Open the installed app and tap
            Enable notifications when you want browser push alerts.
          </Text>
        </View>
        <View style={styles.grid}>
          {installGuides.map((guide) => (
            <View key={guide.title} style={styles.guideCard}>
              <DeviceIllustration accent={guide.accent} icon={guide.icon} />
              <View style={styles.guideCopy}>
                <View style={styles.guideHeader}>
                  <Text style={styles.guideTitle}>{guide.title}</Text>
                  {guide.video ? (
                    <Button
                      title="Watch video"
                      variant="secondary"
                      style={styles.watchVideoButton}
                      onPress={() => setActiveVideo(guide.video ?? null)}
                    />
                  ) : null}
                </View>
                <View style={styles.steps}>
                  {guide.steps.map((step, index) => (
                    <View key={step.title} style={styles.step}>
                      <View style={styles.stepNumber}>
                        <Text style={styles.stepNumberText}>{index + 1}</Text>
                      </View>
                      <View style={styles.stepCopy}>
                        <Text style={styles.stepTitle}>{step.title}</Text>
                        <Text style={styles.stepBody}>{step.body}</Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          ))}
        </View>
        <Link href="/sign-in" asChild>
          <Button title="Go to Frienzzi" />
        </Link>
      </PublicPageShell>
      <Modal
        animationType="fade"
        transparent
        visible={Boolean(activeVideo)}
        onRequestClose={() => setActiveVideo(null)}
      >
        <Pressable
          style={styles.videoBackdrop}
          onPress={() => setActiveVideo(null)}
        >
          <Pressable
            style={styles.videoSheet}
            onPress={(event) => event.stopPropagation()}
          >
            <View style={styles.videoHeader}>
              <Text style={styles.videoTitle}>
                {activeVideo?.title ?? 'Install Frienzzi'}
              </Text>
              <Pressable
                accessibilityLabel="Close install video"
                accessibilityRole="button"
                onPress={() => setActiveVideo(null)}
                style={styles.videoCloseButton}
              >
                <X color={colors.text} size={22} strokeWidth={2.6} />
              </Pressable>
            </View>
            <View style={styles.videoFrame}>
              {activeVideo ? <InstallVideoPlayer video={activeVideo} /> : null}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  deviceButton: {
    backgroundColor: colors.primary,
    borderRadius: 999,
    height: 16,
    marginTop: spacing.md,
    width: 84,
  },
  deviceLine: {
    backgroundColor: colors.border,
    borderRadius: 999,
    height: 10,
    marginTop: spacing.sm,
    width: 92,
  },
  deviceLineWide: {
    backgroundColor: colors.border,
    borderRadius: 999,
    height: 10,
    marginTop: spacing.lg,
    width: 132,
  },
  deviceLogo: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: colors.text,
    borderRadius: 999,
    height: 70,
    justifyContent: 'center',
    marginTop: spacing.lg,
    width: 70,
  },
  deviceShell: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 22,
    height: 210,
    padding: spacing.md,
    width: 150,
  },
  deviceTopBar: {
    backgroundColor: colors.surfacePressed,
    borderRadius: 999,
    height: 8,
    width: 56,
  },
  grid: {
    gap: spacing.lg,
  },
  guideCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  guideCopy: {
    gap: spacing.md,
    padding: spacing.lg,
  },
  guideHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  guideTitle: {
    color: colors.text,
    flexShrink: 1,
    fontSize: 22,
    fontWeight: '900',
    minWidth: 0,
  },
  heroCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  heroText: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 23,
  },
  heroTitle: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '900',
  },
  illustration: {
    alignItems: 'center',
    minHeight: 250,
    justifyContent: 'center',
    padding: spacing.xl,
  },
  nativeVideoFallback: {
    gap: spacing.md,
  },
  nativeVideoPoster: {
    aspectRatio: 720 / 1564,
    borderRadius: 8,
    width: '100%',
  },
  nativeVideoText: {
    color: colors.secondary,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  step: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  stepBody: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 21,
  },
  stepCopy: {
    flex: 1,
    gap: spacing.xs,
  },
  stepNumber: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    height: 30,
    justifyContent: 'center',
    width: 30,
  },
  stepNumberText: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: '900',
  },
  steps: {
    gap: spacing.md,
  },
  stepTitle: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '900',
  },
  videoBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(13, 9, 43, 0.68)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.md,
  },
  videoCloseButton: {
    alignItems: 'center',
    borderRadius: 999,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  videoFrame: {
    alignSelf: 'center',
    aspectRatio: 720 / 1564,
    backgroundColor: '#000000',
    borderRadius: 8,
    maxHeight: '78%',
    maxWidth: 420,
    overflow: 'hidden',
    width: '100%',
  },
  videoHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  videoSheet: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: spacing.md,
    maxHeight: '92%',
    maxWidth: 520,
    padding: spacing.md,
    width: '100%',
  },
  videoTitle: {
    color: colors.text,
    flex: 1,
    fontSize: 18,
    fontWeight: '900',
  },
  watchVideoButton: {
    minHeight: 42,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
});
